// Los dos cierres mensuales que lleva un período de inventario. Lo normal es
// registrarlos juntos con el completo, y por separado cuando materia prima se
// entrega antes de que bodega termine de recibir producción. El valor es el que
// entiende el backend
export const TIPO_MATERIA_PRIMA = 1;
export const TIPO_BODEGA = 2;

// Opción de la pantalla de registro que registra los dos tipos en un solo paso.
// No es un tipo de cierre: el backend la traduce en los dos cierres
export const TIPO_COMPLETO = "completo";

export const TIPOS_CIERRE_MENSUAL = [
    { value: TIPO_MATERIA_PRIMA, label: "Materia prima" },
    { value: TIPO_BODEGA, label: "Bodega de producción" },
];

// solo el cierre de bodega declara una fecha de corte; el de materia prima ya
// queda delimitado por su propio período
export const esTipoBodega = (tipo) => tipo === TIPO_BODEGA;

// Los cierres que registra la opción elegida en la pantalla de registro, igual
// que los interpreta el backend
export const tiposDelRegistro = (tipo) =>
    tipo === TIPO_COMPLETO ? [TIPO_MATERIA_PRIMA, TIPO_BODEGA] : [tipo];

// el registro pide la fecha de corte y las observaciones por producto cuando
// incluye el cierre de bodega
export const incluyeBodega = (tipo) =>
    tiposDelRegistro(tipo).includes(TIPO_BODEGA);

// Opciones del registro según los tipos que admite el período. El completo va
// primero porque es el caso normal, y solo aparece cuando admite los dos
export const opcionesTipoRegistro = (tiposDisponibles) => {
    const opciones = TIPOS_CIERRE_MENSUAL.filter((opcion) =>
        tiposDisponibles.includes(opcion.value),
    ).map((opcion) => ({
        value: opcion.value,
        label: "Solo " + opcion.label.toLowerCase(),
    }));

    if (opciones.length === TIPOS_CIERRE_MENSUAL.length) {
        opciones.unshift({
            value: TIPO_COMPLETO,
            label: "Completo (materia prima y bodega)",
        });
    }

    return opciones;
};

export const nombreTipoCierre = (tipo) => {
    const encontrado = TIPOS_CIERRE_MENSUAL.find(
        (opcion) => opcion.value === tipo,
    );

    return encontrado ? encontrado.label : "";
};

// Lo que se registra, como se nombra en el aviso y en la confirmación: los dos
// cierres en el completo o el del tipo elegido
export const descripcionRegistro = (tipo) => {
    const nombres = tiposDelRegistro(tipo)
        .map((tipoCierre) => nombreTipoCierre(tipoCierre).toLowerCase())
        .join(" y ");

    return tipo === TIPO_COMPLETO
        ? "cierres mensuales de " + nombres
        : "cierre mensual de " + nombres;
};

// Estados con los que se filtra el historial: qué mitades tiene registradas el
// período, que es lo que se pregunta cuando la fila es el período entero. El
// valor es el que entiende el backend
export const ESTADOS_PERIODO_CIERRE_MENSUAL = [
    { value: "completo", label: "Completos" },
    { value: "pendiente_materia_prima", label: "Falta materia prima" },
    { value: "pendiente_bodega", label: "Falta bodega" },
];

// El reporte es del período completo, así que cualquiera de sus dos cierres sirve
// para pedirlo: el backend reconstruye las dos mitades a partir del período
export const cierreParaReporte = (periodo) =>
    periodo.cierre_materia_prima || periodo.cierre_bodega_produccion;

// Cómo se rotula una de las dos mitades del período en el historial: registrada
// o pendiente. Se devuelve como props del Badge global para que la fila y su
// desglose se vean iguales
export const estadoCierreMensual = (cierre) =>
    cierre
        ? { text: "Registrado", variant: "success" }
        : { text: "Pendiente", variant: "secondary" };

// El detalle entra por un cierre puntual pero muestra el período completo, así
// que los datos de cada tipo se buscan en la lista de cierres del período
export const cierreDelPeriodo = (cierres, tipo) =>
    (cierres || []).find((cierre) => cierre.tipo === tipo) || null;

// Cómo se rotula el período según las mitades que ya tiene registradas, también
// como props del Badge global
export const estadoPeriodoCierre = (cierres) => {
    const materiaPrima = cierreDelPeriodo(cierres, TIPO_MATERIA_PRIMA);
    const bodega = cierreDelPeriodo(cierres, TIPO_BODEGA);

    if (materiaPrima && bodega) {
        return { text: "Completo", variant: "success" };
    }

    return {
        text: materiaPrima
            ? "Falta bodega de producción"
            : "Falta materia prima",
        variant: "warning",
    };
};
